import { MantineProvider, mergeMantineTheme } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import "dayjs/locale/pt-br";
import { Provider as JotaiProvider } from "jotai";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { store as jotaiStore } from "./atoms";
import OutdatedClientNotice from "./components/OutdatedClientNotice";
import SentryUserIdentity from "./components/SentryUserIdentity";
import { queryClient } from "./lib/queryClient";
import { router } from "./router";
import { ClientContext, ThemeModeContext } from "./utils/Contexts";
import {
  ThemeMode,
  cssVariablesResolver,
  getThemeClientOverrides,
  mantineTheme,
} from "./utils/Theme";

import "@fontsource/inter/100.css";
import "@fontsource/inter/200.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource/inter/900.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";

const App = () => {
  const { i18n } = useTranslation();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", false, {
    getInitialValueInEffect: false,
  });
  const [mode, setMode] = useState<ThemeMode>(
    prefersDarkMode ? "dark" : "light",
  );
  const themeMode = useMemo(
    () => ({
      toggleThemeMode: () =>
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light")),
    }),
    [],
  );

  const [client, setClient] = useState<string>("");
  const currentClient = useMemo(
    () => ({ set: (overides: string) => setClient(overides) }),
    [],
  );

  // Map i18n language to dayjs locale (dayjs uses lowercase)
  const datesLocale = i18n.language === "pt-BR" ? "pt-br" : "en";

  return (
    <JotaiProvider store={jotaiStore}>
      <QueryClientProvider client={queryClient}>
        <ThemeModeContext.Provider value={themeMode}>
          <ClientContext.Provider value={currentClient}>
            <MantineProvider
              theme={mergeMantineTheme(
                mantineTheme,
                getThemeClientOverrides(client),
              )}
              forceColorScheme={mode === "dark" ? "dark" : "light"}
              cssVariablesResolver={cssVariablesResolver}
            >
              <DatesProvider settings={{ locale: datesLocale }}>
                <RouterProvider router={router} />
                <OutdatedClientNotice />
                <SentryUserIdentity />
              </DatesProvider>
            </MantineProvider>
          </ClientContext.Provider>
        </ThemeModeContext.Provider>
      </QueryClientProvider>
    </JotaiProvider>
  );
};

export default App;
